import React, { useState, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Box, Card, Typography, Divider, Collapse } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'

import { useStyles } from '../useOfferSetupPage'

import AddressPicker from '~/address/components/AddressPicker'
import { clientSearchResponseAddressStore } from '~/address/models/address.model'
import { selectAddressFromClientSearch } from '~/address/store/address.selectors'
import { selectIsOffline } from '~/agent/store/agent.selectors'
import BundlesSummaryOffer from '~/bundles/components/BundlesSummaryOffer/BundlesSummaryOffer'
import { RootState } from '~/core/store/root.reducer'
import SignUpForm from '~/line/components/SignUpForm/SignUpForm'
import TaxSelector from '~/prices/components/TaxSelector'
import { selectIsPostpaid, selectIsCartera } from '~/sale/store/sale.selectors'
import TariffPicker from '~/tariff/components/TariffPicker/TariffPicker'
import { LineTypes } from '~/tariff/model/tariff.model'
import { selectHasSelectedTariff } from '~/tariff/store/tariff.selectors'

export default function Package({
  id,
  onDelete,
  shouldDisable,
  onDeleteClientSearchAddress,
}: {
  id: string
  onDelete?: (id: string) => void
  shouldDisable?: boolean
  onDeleteClientSearchAddress?: (id: string, addressType: string) => void
}) {
  const hasSelectedTariff = useSelector((state: RootState) => selectHasSelectedTariff(state, id))
  const isOffline = useSelector(selectIsOffline)
  const isPostpaid = useSelector(selectIsPostpaid)
  const isCartera = useSelector(selectIsCartera)
  const [isClientSearchAddressModified, setIsClientSearchAddressModified] = useState(false)
  const addressFromClientSearch: clientSearchResponseAddressStore = useSelector(
    selectAddressFromClientSearch,
  )
  const shouldShowClientSearchAddress = useMemo(() => {
    return (
      !isClientSearchAddressModified &&
      isCartera &&
      id === LineTypes.MAIN &&
      addressFromClientSearch.streetName !== ''
    )
  }, [isClientSearchAddressModified, isCartera, id, addressFromClientSearch])

  const onModifyMainLineAddress = () => {
    setIsClientSearchAddressModified(true)
    return onDeleteClientSearchAddress ? onDeleteClientSearchAddress(id, 'SIM') : null
  }

  const { overlay, border } = useStyles()

  return (
    <Box mb="24px" data-hook={id} position="relative">
      <Card elevation={shouldDisable ? 0 : 5} className={shouldDisable ? border : ''}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          py="20px"
          px="24px"
        >
          <Typography variant="h3">Línea {isPostpaid ? 'Pospago' : 'Prepago'}</Typography>
          {id !== LineTypes.MAIN ? (
            <CloseIcon
              style={{ cursor: 'pointer', fontSize: '32px' }}
              onClick={() => onDelete && onDelete(id)}
            />
          ) : (
            <TaxSelector />
          )}
        </Box>
        <Divider />
        <TariffPicker id={id} />
        <Collapse in={hasSelectedTariff}>
          <BundlesSummaryOffer id={id} />
          <SignUpForm hasSelectedTariff={hasSelectedTariff} id={id} />
          {!isOffline && (
            <>
              <Box px={6} pt={6}>
                <Divider className="dotted" />
                <Box py={6}>
                  <Typography variant="subtitle2">DIRECCIÓN DE ENTREGA DE LA SIM</Typography>
                </Box>
              </Box>
              <Box pb={6}>
                {shouldShowClientSearchAddress ? (
                  <Box px={6} display="flex" alignItems="center">
                    <Typography variant="body1">{addressFromClientSearch?.summary}</Typography>
                    <Box ml={6}>
                      <Typography onClick={onModifyMainLineAddress} className="link">
                        Modificar
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <AddressPicker id={id} />
                )}
              </Box>
            </>
          )}
        </Collapse>
      </Card>
      {shouldDisable && <div data-hook={`${id}-disabled-overlay`} className={overlay} />}
    </Box>
  )
}
